import { ref } from "vue";
import debounce from "lodash.debounce";

/**
 * Creates a reactive remote autocomplete search functionality. This is a reusable composable.
 *
 * @param {Function} apiCallback - A function that performs the API call for the search.
 *                                 It should take a search value as an argument and return a Promise.
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowFreeText - Whether to allow values not in the autocomplete results
 * @returns {Object} An object containing reactive references and a search update function:
 *                   - valueRef: Ref<string> - The current search value.
 *                   - errorRef: Ref<Error|undefined> - Any error that occurred during the search.
 *                   - loadingRef: Ref<boolean> - Indicates whether a search is in progress.
 *                   - itemsRef: Ref<Array> - The search results.
 *                   - searchUpdate: Function - A debounced function to update the search.
 */
export const useRemoteAutocompleteSearch = (apiCallback, options = {}) => {
  const itemsRef = ref([]);
  const valueRef = ref("");
  const searchRef = ref("");
  const errorRef = ref(undefined);
  const loadingRef = ref(false);
  const allowFreeText = options.allowFreeText || false;
  let lastSearchValue = undefined;

  const isSearchValueSelected = (search) => {
    const searchItem = itemsRef.value.find((v) => v.title === search);
    return searchItem && searchItem.value === valueRef.value;
  };

  const debouncedApi = debounce((value) => {
    if (!value) {
      loadingRef.value = false;
      return;
    }
    const promise = apiCallback(value);
    if (promise) {
      promise
        .then((response) => {
          loadingRef.value = false;
          itemsRef.value = response;
          // If free text is allowed, we set the value even if it's not in the dropdown
          if (allowFreeText && searchRef.value) {
            valueRef.value = searchRef.value;
          }
        })
        .catch((err) => {
          loadingRef.value = false;
          itemsRef.value = [];
          errorRef.value = err;
        });
    } else {
      loadingRef.value = false;
    }
  }, 500);
  // The update:search listener
  // Should make the API request and update the "items"
  const searchUpdate = (value) => {
    if (lastSearchValue === value) {
      // Have we already searched for this value?
      return;
    }
    if (isSearchValueSelected(value)) {
      // Is the text value the same as the selected value
      // then we don't want to search again, since this was a selection
      searchRef.value = valueRef.value;
      return;
    } else if (allowFreeText) {
      valueRef.value = value;
    }
    loadingRef.value = true;
    searchRef.value = value;
    lastSearchValue = value;
    debouncedApi(value);
  };

  const reset = (initialValue) => {
    valueRef.value = initialValue;
    searchRef.value = initialValue;
    errorRef.value = undefined;
    loadingRef.value = false;
    itemsRef.value = [];
    lastSearchValue = undefined;
    // Run a search so the dropdown is prepopulated with the initial value
    searchUpdate(initialValue);
  };

  return { valueRef, searchRef, errorRef, loadingRef, itemsRef, searchUpdate, reset };
};
